/**
 * D01 — Home.
 *
 * A hero, the two things someone opens the app to do as large green cards —
 * order a tiffin, support a meal — two lighter tiles for New Features and
 * My Orders, the three assurances, and How It Works to close.
 *
 * Every size comes from `homeLayout`, worked out from the column the screen
 * actually has, so it holds from a 320pt phone to a desktop browser without a
 * phone layout being stretched or squeezed. On a phone tall enough, Home then
 * fits itself to the screen with no scrolling (useFitToScreen): only the
 * space between and inside the blocks tightens, never the type. On a short
 * phone the hero and the cards tighten so both green cards stay above the
 * fold; the rest scrolls, which beats clipping — accessibility wins that
 * argument.
 */

import { useCallback } from 'react';
import { Alert, Platform, StyleSheet, View, useWindowDimensions } from 'react-native';

import { colors, radius, space } from '../../theme';
import { ComingSoonBadge } from '../../components/common/ComingSoonBadge';
import {
  ActionCard,
  Assurance,
  AssuranceRule,
  GuideBar,
  TileCard,
  homeLayout,
  useTallestTitle,
} from '../../components/home/HomeCards';
import {
  Blob,
  CareArt,
  HeroArt,
  NewFeaturesArt,
  OrdersArt,
  SafeArt,
  SaladArt,
  SupportArt,
  TiffinArt,
} from '../../components/home/HomeArt';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Text } from '../../components/common/Text';
import { useAppConfig } from '../../core/api/hooks';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useLanguage, useT } from '../../core/i18n/LanguageProvider';
import { useFitToScreen } from './useFitToScreen';

export const MORE_FEATURES_TITLE = 'More features coming soon';
export const MORE_FEATURES_BODY =
  'We are building more ways to give and to follow your donation. ' +
  'In the meantime you can sponsor a meal and track it all the way to the people who receive it.';

/**
 * The space between two of Home's blocks. On a tall screen each grows by up
 * to `GAP_GROWTH`, so spare height is shared out between the blocks rather
 * than gathering in one empty band; beyond that it falls below the last one.
 */
const GAP_GROWTH = space['4xl'];

function Gap({ height }: { height: number }) {
  return <View style={[styles.gap, { minHeight: height, maxHeight: height + GAP_GROWTH }]} />;
}

export function HomeScreen({ navigation }: DonorScreenProps<'Home'>) {
  const t = useT();
  const { language, isRTL } = useLanguage();
  const { height, width, fontScale } = useWindowDimensions();
  // Tiffin ordering is offered only once the kitchen has set it up, so no one
  // is sent to a menu with nothing on it.
  const { data: config, isPending: configPending } = useAppConfig();
  const tiffinOpen = Boolean(config?.tiffin?.available);
  // On a first visit, before the server has said whether the kitchen is
  // open, the card holds its place rather than arriving late and pushing
  // everything below it down.
  const tiffinPending = configPending && !config;
  const tiffinShown = tiffinOpen || tiffinPending;

  // Anything that changes how tall the blocks are starts the fit again.
  const fit = useFitToScreen(
    `${width}x${height}:${fontScale}:${language}:${tiffinShown}`,
    (squeeze) => {
      const { gap } = homeLayout(width, height, squeeze);
      return gap.blocks + gap.tiles + 2 * gap.assurances;
    },
  );
  const layout = homeLayout(width, height, fit.squeeze);
  // Re-measured whenever the column or the language changes what wraps.
  const titles = useTallestTitle(`${layout.column}:${language}`);

  const onMoreFeatures = useCallback(() => {
    // Never navigate to a screen that is not built. An alert says the true
    // thing; a dead route would look like a bug.
    if (Platform.OS === 'web') {
      // React Native Web's Alert has no button callbacks, so the browser's own
      // dialog is the honest fallback rather than a silent no-op.
      // eslint-disable-next-line no-alert
      window.alert(`${t(MORE_FEATURES_TITLE)}\n\n${t(MORE_FEATURES_BODY)}`);
      return;
    }
    Alert.alert(t(MORE_FEATURES_TITLE), t(MORE_FEATURES_BODY), [
      { text: t('Close'), style: 'cancel' },
    ]);
  }, [t]);

  const { hero, assurance, gap } = layout;

  return (
    <ScreenShell
      leading="menu"
      onMenuPress={() => navigation.getParent()?.dispatch({ type: 'OPEN_DRAWER' })}
      onBodySpace={fit.onBodySpace}
      contentStyle={fit.ready ? null : styles.measuring}
      testID="screen-home"
    >
      <View
        style={[styles.hero, { padding: hero.padding, paddingEnd: 0 }]}
        onLayout={fit.onBlockLayout('hero')}
        testID="home-hero"
      >
        <View style={[styles.heroWords, { marginEnd: space.sm }]}>
          <Text variant="hero" tone="brand" size={hero.title} testID="home-hero-title">
            {t('A Meal from You,\nA Blessing for Them')}
          </Text>
          <Text variant="heroSubtitle" tone="secondary" size={hero.subtitle} style={styles.heroSubtitle}>
            {t("Your small contribution can fill someone's empty plate.")}
          </Text>
          <View style={styles.heroRule} />
        </View>
        {/* Held up from the card's bottom edge, as in the artwork, and turned
            to face the words: in Urdu the drawing is on the left, and its
            leaves would otherwise be cut by the card's edge rather than
            reaching towards the text. */}
        <View style={[styles.heroArt, { marginBottom: -hero.padding }, isRTL && styles.mirrored]}>
          <HeroArt size={hero.art} />
        </View>
      </View>

      <Gap height={gap.blocks} />
      <View style={{ gap: gap.cards }} onLayout={fit.onBlockLayout('cards')}>
        {tiffinShown ? (
          <ActionCard
            title={t('Order a Tiffin')}
            description={t('Get a nutritious meal delivered to your doorstep.')}
            art={(size) => <TiffinArt size={size} />}
            layout={layout.action}
            onPress={() => navigation.navigate('TiffinMenu')}
            disabled={!tiffinOpen}
            accessibilityHint="Order breakfast, lunch or dinner for yourself"
            testID="cta-order-tiffin"
          />
        ) : null}
        <ActionCard
          title={t('Support a Meal')}
          description={t('Contribute towards meals for those in need.')}
          art={(size) => <SupportArt size={size} />}
          layout={layout.action}
          onPress={() => navigation.navigate('DonateMeal')}
          accessibilityHint="Choose a meal to sponsor for people in need"
          testID="cta-donate"
        />
      </View>

      <Gap height={gap.tiles} />
      <View style={styles.tiles} onLayout={fit.onBlockLayout('tiles')}>
        <TileCard
          title={t('New Features')}
          description={t("Explore what's new in Meal4ALL.")}
          art={(size) => <NewFeaturesArt size={size} />}
          layout={layout.tile}
          tint={colors.bg.highlight}
          onPress={onMoreFeatures}
          accessibilityHint="More features are coming soon"
          // Straddling the top border, so the tile keeps the height of its
          // neighbour and the tag reads as attached to it.
          badge={<ComingSoonBadge style={styles.comingSoon} />}
          testID="cta-new-features"
        />
        <TileCard
          title={t('My Orders')}
          description={t('View and track your orders.')}
          art={(size) => <OrdersArt size={size} />}
          layout={layout.tile}
          tint={colors.bg.selected}
          onPress={() => navigation.navigate('MyOrders')}
          accessibilityHint="See the donations and tiffin orders made from this device"
          testID="cta-my-orders"
        />
      </View>

      <Gap height={gap.assurances} />
      <View style={styles.assurances} onLayout={fit.onBlockLayout('assurances')} testID="home-assurances">
        <Assurance
          art={
            <Blob size={assurance.blob} tint={colors.bg.brandSubtle}>
              {(inner) => <SaladArt size={inner} />}
            </Blob>
          }
          title={t('Nutritious Meals')}
          description={t('Healthy and balanced meals for a better tomorrow.')}
          layout={assurance}
          titleHeight={titles.minHeight}
          onTitleLayout={titles.onLayout(0)}
        />
        <AssuranceRule />
        <Assurance
          art={
            <Blob size={assurance.blob} tint={colors.status.info.bg}>
              {(inner) => <SafeArt size={inner} />}
            </Blob>
          }
          title={t('Hygienic & Safe')}
          description={t('Meals are prepared with care and hygiene.')}
          layout={assurance}
          titleHeight={titles.minHeight}
          onTitleLayout={titles.onLayout(1)}
        />
        <AssuranceRule />
        <Assurance
          art={
            <Blob size={assurance.blob} tint={colors.status.danger.bg}>
              {(inner) => <CareArt size={inner} />}
            </Blob>
          }
          title={t('Delivered with Care')}
          description={t('Your contribution reaches those in need with love and care.')}
          layout={assurance}
          titleHeight={titles.minHeight}
          onTitleLayout={titles.onLayout(2)}
        />
      </View>

      <Gap height={gap.assurances} />

      <View onLayout={fit.onBlockLayout('guide')}>
        <GuideBar
          label={t('How It Works')}
          height={layout.guide.height}
          onPress={() => navigation.navigate('HowItWorks')}
          accessibilityHint="See how ordering and supporting a meal work"
          testID="cta-how-it-works"
        />
      </View>
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  hero: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: radius.xl,
    backgroundColor: colors.bg.brandSubtle,
    overflow: 'hidden',
  },
  heroWords: {
    flex: 1,
    minWidth: 0,
  },
  heroSubtitle: {
    marginTop: space.sm,
  },
  heroArt: {
    alignSelf: 'flex-end',
  },
  mirrored: {
    transform: [{ scaleX: -1 }],
  },
  heroRule: {
    width: space['4xl'] + space.lg,
    height: space.xs,
    marginTop: space.md,
    borderRadius: radius.full,
    backgroundColor: colors.bg.accent,
  },
  tiles: {
    flexDirection: 'row',
    gap: space.md,
  },
  comingSoon: {
    position: 'absolute',
    top: -space.sm,
    // The end, not the right: in Urdu the tag moves with the arrow.
    end: space.md,
  },
  assurances: {
    flexDirection: 'row',
  },
  gap: {
    flexGrow: 1,
  },
  // Laid out and measured, but not yet shown: the first frames are spent
  // fitting Home to the screen.
  measuring: {
    opacity: 0,
  },
});
